import React, { useState, useCallback, useMemo } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import { PriorityBadge } from '../../work/components/PriorityBadge';
import { useFilingQueue, type QueueTab, type QueueItem } from './useFilingQueue';

interface AdminFilingQueueProps {
  brand: BrandConfig;
  Icons: IconsType;
  onNavigate: (module: string, subSection?: string) => void;
}

const TAB_DEFS: { id: QueueTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'unassigned', label: 'Unassigned' },
  { id: 'my-queue', label: 'My queue' },
  { id: 'on-hold', label: 'On hold' },
  { id: 'rejected', label: 'Rejected' },
];

function formatSla(minutesLeft: number): string {
  if (minutesLeft <= 0) {
    const overdue = Math.abs(minutesLeft);
    if (overdue < 60) return `${overdue}m overdue`;
    const h = Math.floor(overdue / 60);
    if (h >= 48) {
      const d = Math.floor(h / 24);
      const rh = h % 24;
      return rh > 0 ? `${d}d ${rh}h overdue` : `${d}d overdue`;
    }
    const m = overdue % 60;
    return m > 0 ? `${h}h ${m}m overdue` : `${h}h overdue`;
  }
  if (minutesLeft < 60) return `${minutesLeft}m left`;
  const h = Math.floor(minutesLeft / 60);
  if (h >= 48) {
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
  }
  const m = minutesLeft % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function getSlaColor(item: QueueItem): string {
  if (item.slaBreached) return '#DC2626';
  if (item.slaAtRisk) return '#D97706';
  if (item.slaMinutesLeft < 120) return '#D97706';
  return '#6B7280';
}

function getStatusStyle(status: string): { bg: string; text: string } {
  if (status === 'In review') return { bg: '#DBEAFE', text: '#1E40AF' };
  if (status === 'New') return { bg: '#F3F4F6', text: '#374151' };
  if (status === 'Rejected') return { bg: '#FEE2E2', text: '#991B1B' };
  if (status === 'Accepted') return { bg: '#D1FAE5', text: '#065F46' };
  if (status.startsWith('Hold')) return { bg: '#FEF3C7', text: '#92400E' };
  return { bg: '#F3F4F6', text: '#374151' };
}

const CrownIcon: React.FC = () => (
  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="#7C3AED">
    <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm0 2h14v2H5v-2z"/>
  </svg>
);

const AdminFilingQueue: React.FC<AdminFilingQueueProps> = ({ brand, Icons, onNavigate }) => {
  const [activeTab, setActiveTab] = useState<QueueTab>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  // Debounce search
  const searchTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(0);
    }, 400);
  }, []);

  const {
    items,
    total,
    tabCounts,
    kpis,
    isLoading,
    error,
    holdOrder,
    releaseOrder,
  } = useFilingQueue({
    tab: activeTab,
    priority: priorityFilter || undefined,
    search: debouncedSearch || undefined,
    page,
    perPage: 25,
  });

  const handleTabChange = useCallback((tab: QueueTab) => {
    setActiveTab(tab);
    setPage(0);
    setSelectedIds(new Set());
  }, []);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(i => i.id)));
    }
  }, [items, selectedIds.size]);

  const totalPages = Math.ceil(total / 25);

  const tabCountMap = useMemo(() => ({
    all: tabCounts.all,
    unassigned: tabCounts.unassigned,
    'my-queue': tabCounts.myQueue,
    'on-hold': tabCounts.onHold,
    rejected: tabCounts.rejected,
  }), [tabCounts]);

  return (
    <div className="flex-1 overflow-auto" style={{ backgroundColor: brand.colors.bgLight }}>
      <div className="p-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1
              className="text-2xl font-semibold"
              style={{ color: brand.colors.secondary, fontFamily: brand.typography.fontFamily.heading }}
            >
              Filing queue
            </h1>
            <p className="text-sm mt-1" style={{ color: brand.colors.textLight }}>
              All filings awaiting operator action — sorted by priority and age.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="px-4 py-2 text-sm font-medium rounded-lg border"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            >
              Export
            </button>
            {tabCounts.unassigned > 0 && (
              <button
                className="px-4 py-2 text-sm font-medium rounded-lg"
                style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
              >
                Auto-assign {tabCounts.unassigned} unassigned
              </button>
            )}
          </div>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <KpiCard
            label="In queue"
            value={kpis.inQueue}
            detail={`${kpis.urgentCount} urgent`}
            brand={brand}
          />
          <KpiCard
            label="Avg. time to dispatch"
            value="—"
            detail="Tracking not yet enabled"
            brand={brand}
          />
          <KpiCard
            label="Assigned to me"
            value={kpis.assignedToMe}
            brand={brand}
          />
          <KpiCard
            label="SLA breach risk"
            value={kpis.slaBreachRisk}
            valueColor={kpis.slaBreachRisk > 0 ? '#DC2626' : undefined}
            detail={kpis.slaBreachRisk > 0 ? 'Resolve within 60 min' : 'All clear'}
            brand={brand}
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4">
          {TAB_DEFS.map(t => {
            const count = tabCountMap[t.id];
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => handleTabChange(t.id)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                style={{
                  backgroundColor: isActive ? brand.colors.primary : 'transparent',
                  color: isActive ? brand.colors.onPrimary : brand.colors.text,
                }}
              >
                {t.label}
                <span
                  className="px-1.5 py-0.5 rounded text-xs font-medium"
                  style={{
                    backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : brand.colors.bgLight,
                    color: isActive ? brand.colors.onPrimary : brand.colors.textLight,
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-3">
          <input
            type="text"
            placeholder="Search order, case, customer"
            value={searchValue}
            onChange={e => handleSearchChange(e.target.value)}
            className="px-3 py-2 text-sm border rounded-lg w-64"
            style={{ borderColor: brand.colors.border }}
          />
          <select
            value={priorityFilter}
            onChange={e => { setPriorityFilter(e.target.value); setPage(0); }}
            className="px-3 py-2 text-sm border rounded-lg"
            style={{ borderColor: brand.colors.border }}
          >
            <option value="">All priorities</option>
            <option value="urgent">Urgent</option>
            <option value="priority">Priority</option>
            <option value="standard">Standard</option>
            <option value="on-demand">On-Demand</option>
          </select>
          {(searchValue || priorityFilter) && (
            <button
              onClick={() => { setSearchValue(''); setDebouncedSearch(''); setPriorityFilter(''); setPage(0); }}
              className="text-sm font-medium"
              style={{ color: brand.colors.textLight }}
            >
              Clear
            </button>
          )}
        </div>

        {/* Bulk Actions */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 mb-2 text-xs" style={{ color: brand.colors.textMuted }}>
            <span><strong>{selectedIds.size} selected</strong> ·</span>
            <button className="font-medium" style={{ color: brand.colors.link }}>Assign to…</button>
            <button className="font-medium" style={{ color: brand.colors.link }}>Change priority…</button>
            <button
              className="font-medium"
              style={{ color: brand.colors.link }}
              onClick={async () => {
                for (const id of selectedIds) {
                  await holdOrder(id);
                }
                setSelectedIds(new Set());
              }}
            >
              Put on hold
            </button>
            <button className="font-medium" style={{ color: '#DC2626' }}>Cancel</button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-800 text-sm" role="alert">
            {error}
          </div>
        )}

        {/* Table */}
        <div
          className="rounded-xl shadow-xs overflow-hidden"
          style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}>
                <th className="w-10 px-3 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={items.length > 0 && selectedIds.size === items.length}
                    onChange={handleToggleAll}
                    className="rounded"
                  />
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider">Order</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider">Customer / Account</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider">Case & Court</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider">Priority</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider">SLA</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider">Assignee</th>
                <th className="w-12 px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                ['sk-a','sk-b','sk-c','sk-d','sk-e'].map((rowKey, i) => (
                  <tr key={rowKey} style={{ borderTop: i > 0 ? `1px solid ${brand.colors.border}` : undefined }}>
                    {['c0','c1','c2','c3','c4','c5','c6','c7','c8'].map(cellKey => (
                      <td key={cellKey} className="px-3 py-3">
                        <div className="h-4 rounded bg-gray-200 animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center" style={{ color: brand.colors.textLight }}>
                    No items in queue
                  </td>
                </tr>
              ) : (
                items.map((item, idx) => {
                  const slaColor = getSlaColor(item);
                  const statusStyle = getStatusStyle(item.status);
                  const isUrgent = item.priority === 'urgent';

                  return (
                    <tr
                      key={item.id}
                      className="group hover:bg-gray-50 transition-colors"
                      style={{
                        borderTop: idx > 0 ? `1px solid ${brand.colors.border}` : undefined,
                        borderLeft: isUrgent ? '3px solid #DC2626' : item.accountType === 'concierge' ? '3px solid #7C3AED' : '3px solid transparent',
                      }}
                    >
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.id)}
                          onChange={() => handleToggleSelect(item.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <button
                          onClick={() => onNavigate('orders', item.id)}
                          className="font-semibold hover:underline"
                          style={{ color: brand.colors.link }}
                        >
                          {item.orderNumber}
                        </button>
                        <div className="text-xs mt-0.5" style={{ color: brand.colors.textMuted }}>
                          {item.typeLabel}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1 font-medium" style={{ color: brand.colors.text }}>
                          {item.accountType === 'concierge' && <CrownIcon />}
                          {item.account}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-medium" style={{ color: brand.colors.text }}>
                          {item.caseTitle || '—'}
                        </div>
                        {item.caseNumber && (
                          <div className="text-xs mt-0.5" style={{ color: brand.colors.textMuted }}>
                            {item.caseNumber}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <PriorityBadge priority={item.priority} brand={brand} />
                      </td>
                      <td className="px-3 py-3 tabular-nums" style={{ color: slaColor, fontWeight: item.slaBreached || item.slaAtRisk ? 600 : 400 }}>
                        {formatSla(item.slaMinutesLeft)}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className="inline-flex px-2 py-0.5 text-xs font-medium rounded"
                          style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        {item.assignee ? (
                          <span className="inline-flex items-center gap-1.5 bg-gray-50 rounded-full pl-0.5 pr-2.5 py-0.5 text-xs font-medium">
                            <span
                              className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
                              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
                            >
                              {item.assignee.initials}
                            </span>
                            {item.assignee.name.split(' ')[0]} {item.assignee.name.split(' ')[1]?.[0]}.
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs italic" style={{ color: brand.colors.textMuted }}>
                            <span
                              className="w-5 h-5 rounded-full border-[1.5px] border-dashed"
                              style={{ borderColor: brand.colors.border }}
                            />
                            Unassigned
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1">
                          {item.isOnHold ? (
                            <button
                              onClick={() => releaseOrder(item.id)}
                              className="p-1 rounded hover:bg-gray-100 text-xs font-medium"
                              style={{ color: brand.colors.link }}
                              title="Release from hold"
                            >
                              Release
                            </button>
                          ) : (
                            <button
                              onClick={() => onNavigate('orders', item.id)}
                              className="p-1 rounded hover:bg-gray-100"
                              style={{ color: brand.colors.textLight }}
                              title="Open order"
                            >
                              <Icons.ChevronRight className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              className="px-4 py-3 flex items-center justify-between text-xs"
              style={{ borderTop: `1px solid ${brand.colors.border}`, color: brand.colors.textMuted }}
            >
              <div>Showing {items.length} of {total} in queue</div>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-2 py-1 border rounded disabled:opacity-30"
                  style={{ borderColor: brand.colors.border }}
                >
                  &lsaquo;
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => (
                  <button
                    key={`page-${i}`} // eslint-disable-line @eslint-react/no-array-index-key -- page number IS the identity
                    onClick={() => setPage(i)}
                    className="px-2 py-1 border rounded text-xs font-medium"
                    style={{
                      borderColor: page === i ? brand.colors.primary : brand.colors.border,
                      backgroundColor: page === i ? brand.colors.primary : 'transparent',
                      color: page === i ? brand.colors.onPrimary : brand.colors.text,
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-2 py-1 border rounded disabled:opacity-30"
                  style={{ borderColor: brand.colors.border }}
                >
                  &rsaquo;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface KpiCardProps {
  label: string;
  value: number | string;
  detail?: string;
  valueColor?: string;
  brand: BrandConfig;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, detail, valueColor, brand }) => (
  <div
    className="rounded-xl p-4 shadow-xs"
    style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
  >
    <div className="text-xs font-medium uppercase tracking-wider mb-1" style={{ color: brand.colors.textLight }}>
      {label}
    </div>
    <div className="text-2xl font-semibold" style={{ color: valueColor ?? brand.colors.secondary }}>
      {value}
    </div>
    {detail && (
      <div className="text-xs mt-1" style={{ color: brand.colors.textMuted }}>
        {detail}
      </div>
    )}
  </div>
);

export default AdminFilingQueue;
